import React, { useEffect, useState } from 'react';
import moment from 'moment-timezone';

// Helper: convert an ISO/Date to datetime-local string in event timezone
const toLocalInputForTz = (dateLike, tz) => dateLike ? moment.tz(dateLike, tz || 'UTC').format('YYYY-MM-DDTHH:mm') : '';
// Helper: now in event timezone for input min
const nowLocalInputForTz = (tz) => moment.tz(new Date(), tz || 'UTC').format('YYYY-MM-DDTHH:mm');

const EditAdModal = ({ isOpen, post, onClose, onSave, onDelete, companySlug }) => {
  // Helper: append company_slug to API URLs for per-tab company scoping
  const scopeUrl = (url) => {
    if (!companySlug) return url;
    const sep = url.includes('?') ? '&' : '?';
    return `${url}${sep}company_slug=${companySlug}`;
  };
  const [editedPost, setEditedPost] = useState(post || {});
  const [campaigns, setCampaigns] = useState([]);
  const [pages, setPages] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Check if ad is published - if so, disable all fields
  const isPublished = post?.status === 'published' || post?.status === 'scheduled';
  
  // Options for selectors
  const billingEvents = ['IMPRESSIONS', 'LINK_CLICKS', 'POST_ENGAGEMENT', 'VIDEO_VIEWS'];
  const optimizationGoals = ['LINK_CLICKS', 'REACH', 'IMPRESSIONS', 'POST_ENGAGEMENT', 'VIDEO_VIEWS', 'LANDING_PAGE_VIEWS'];
  const bidStrategies = ['LOWEST_COST_WITHOUT_CAP', 'LOWEST_COST_WITH_BID_CAP', 'COST_CAP'];
  
  useEffect(() => {
    if (isOpen) {
      // Fetch campaigns
      fetch(scopeUrl('/api/facebook/campaigns'), { headers: { Accept: 'application/json' } })
        .then(res => res.json())
        .then(data => {
          if (data && Array.isArray(data.campaigns)) {
            setCampaigns(data.campaigns);
          }
        })
        .catch(err => console.error('Error fetching campaigns:', err));
      
      // Fetch pages
      fetch(scopeUrl('/api/facebook/connection_status'), { headers: { Accept: 'application/json' } })
        .then(res => res.json())
        .then(data => {
          if (data && Array.isArray(data.pages)) {
            setPages(data.pages);
          }
        })
        .catch(err => console.error('Error fetching pages:', err));
    }
  }, [isOpen]);
  
  useEffect(() => {
    if (post) {
      // Update editedPost when post prop changes (for runtime updates)
      setEditedPost(prev => ({ ...prev, ...post }));
      
      // Format the date and time for datetime-local input
      let formattedDateTime = '';
      
      // Determine which date to use based on post properties
      const tz = (post?.event && post.event.timezone) || post?.timezone || 'UTC';
      if (post.published_at) {
        formattedDateTime = toLocalInputForTz(post.published_at, tz);
      } else if (post.scheduled_at) {
        formattedDateTime = toLocalInputForTz(post.scheduled_at, tz);
      } else if (post.date) {
        formattedDateTime = toLocalInputForTz(post.date, tz);
      } else {
        formattedDateTime = toLocalInputForTz(new Date(), tz);
      }
      
      // Get data from ad_set and creative
      const adSet = post.ad_set || {};
      const creative = post.creative || {};
      const targeting = adSet.targeting || {};
      
      // Parse geo location from targeting
      let locationString = 'US'; // Default to US
      if (targeting.geo_locations) {
        const geoLocations = targeting.geo_locations;
        
        // Check for cities
        if (geoLocations.cities && Array.isArray(geoLocations.cities) && geoLocations.cities.length > 0) {
          const city = geoLocations.cities[0];
          // Format: "City, Region, Country" or "City, Country"
          if (city.name && city.country) {
            if (city.region) {
              locationString = `${city.name}, ${city.region}, ${city.country}`;
            } else {
              locationString = `${city.name}, ${city.country}`;
            }
          } else if (city.name) {
            locationString = city.name;
          }
        }
        // Check for countries
        else if (geoLocations.countries && Array.isArray(geoLocations.countries) && geoLocations.countries.length > 0) {
          locationString = geoLocations.countries[0];
        }
        // Check for regions
        else if (geoLocations.regions && Array.isArray(geoLocations.regions) && geoLocations.regions.length > 0) {
          const region = geoLocations.regions[0];
          if (typeof region === 'string') {
            locationString = region;
          } else if (region.name && region.country) {
            locationString = `${region.name}, ${region.country}`;
          }
        }
        // If geo_locations exists but is empty or invalid, default to US
        else if (Object.keys(geoLocations).length === 0) {
          locationString = 'US';
        }
      }
      
      // Format dates properly
      let startDateFormatted = '';
      let endDateFormatted = '';
      let scheduledDateFormatted = '';
      
      if (adSet.start_date) {
        startDateFormatted = toLocalInputForTz(adSet.start_date, tz);
      }
      if (adSet.end_date) {
        endDateFormatted = toLocalInputForTz(adSet.end_date, tz);
      }
      if (post.scheduled_at) {
        scheduledDateFormatted = toLocalInputForTz(post.scheduled_at, tz);
      }
      
      setEditedPost({
        ...post,
        scheduledDateTime: formattedDateTime,
        // Campaign
        campaignId: post.campaign_id || '',
        // Page
        pageId: creative.page_id || '',
        // Ad Set
        adSetName: adSet.name || post.adSetName || '',
        budgetStrategy: adSet.budget_strategy || 'daily_budget',
        dailyBudget: adSet.daily_budget ? adSet.daily_budget.toString() : '',
        lifetimeBudget: adSet.lifetime_budget ? adSet.lifetime_budget.toString() : '',
        startDate: startDateFormatted,
        endDate: endDateFormatted,
        scheduledDate: scheduledDateFormatted,
        billingEvent: adSet.billing_event || 'IMPRESSIONS',
        optimizationGoal: adSet.optimization_goal || 'LINK_CLICKS',
        bidStrategy: adSet.bid_strategy || 'LOWEST_COST_WITHOUT_CAP',
        geoLocation: locationString,
        ageMin: targeting.age_min ? targeting.age_min.toString() : '',
        ageMax: targeting.age_max ? targeting.age_max.toString() : '',
        // Creative
        creativeName: creative.name || post.creativeName || '',
        creativeMessage: creative.message || post.message || post.description || '',
        creativeHeadline: creative.headline || post.title || '',
        creativeDescription: creative.description || post.description || '',
        creativeLink: creative.link || '',
        // Ad
        adName: post.name || post.adName || ''
      });
    } else {
      setEditedPost({});
    }
  }, [post]);

  // Reset loading state when modal closes
  useEffect(() => {
    if (!isOpen) {
      setLoading(false);
    }
  }, [isOpen]);

  if (!isOpen) return null;

  const handleChange = (e) => {
    const { name, value } = e.target;
    setEditedPost(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const isFutureMinute = () => {
    if (!editedPost || !editedPost.scheduledDateTime) return false;
    const tz = (editedPost?.event && editedPost.event.timezone) || editedPost?.timezone || 'UTC';
    // Compare entirely within the event timezone (no UTC conversion)
    const scheduledLocal = moment.tz(editedPost.scheduledDateTime, 'YYYY-MM-DDTHH:mm', tz);
    const nowLocal = moment.tz(new Date(), tz);
    return scheduledLocal.isAfter(nowLocal, 'minute');
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    // Prevent submission if ad is published
    if (isPublished) {
      return;
    }
    setLoading(true);
    onSave(editedPost, "update", () => {
      // Callback to reset loading state
      setLoading(false);
    });
  };

  return (
    <div className="modal-overlay">
      <div className="modal-container">
        <div className="edit-post-header">
          <h2>Edit Ad</h2>
          <button className="close-btn" onClick={onClose}>×</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="modal-body" style={{ maxHeight: '70vh', overflowY: 'auto' }}>
            <div className="post-preview">
              {editedPost?.image && (
                editedPost.media_type?.includes("video") ? (
                  <video
                    src={editedPost.image}
                    className="post-preview-image"
                    controls
                    autoPlay
                    muted
                    loop
                    playsInline
                  />
                ) : (
                  <img
                    src={editedPost.image}
                    alt="Post preview"
                    className="post-preview-image"
                  />
                )
              )}
            </div>

            {/* Campaign Selection */}
            <div className="form-group">
              <label>Select Campaign</label>
              <select
                name="campaignId"
                value={editedPost.campaignId || ""}
                onChange={handleChange}
                className="form-control auto-height-select"
                disabled={isPublished}
              >
                <option value="">Select Campaign</option>
                {campaigns.map(campaign => (
                  <option key={campaign.id} value={campaign.id}>
                    {campaign.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Page Selection */}
            <div className="form-group">
              <label>Page Selection</label>
              <select
                name="pageId"
                value={editedPost.pageId || ""}
                onChange={handleChange}
                className="form-control"
                disabled={isPublished}
              >
                <option value="">Select Page</option>
                {pages.map(page => (
                  <option key={page.id} value={page.id}>
                    {page.name}
                  </option>
                ))}
              </select>
            </div>

            {/* Ad Set Name */}
            <div className="form-group">
              <label>Ad Set Name</label>
              <input
                type="text"
                name="adSetName"
                value={editedPost.adSetName || ""}
                onChange={handleChange}
                className="form-control"
                disabled={isPublished}
              />
            </div>

            {/* Budget Strategy */}
            <div className="form-group">
              <label>Budget Strategy</label>
              <select
                name="budgetStrategy"
                value={editedPost.budgetStrategy || "daily_budget"}
                onChange={handleChange}
                className="form-control auto-height-select"
                disabled={isPublished}
              >
                <option value="daily_budget">Daily Budget</option>
                <option value="lifetime_budget">Lifetime Budget</option>
              </select>
            </div>

            {/* Daily Budget Fields */}
            {editedPost.budgetStrategy === 'daily_budget' && (
              <div className="form-group">
                <label>Daily Budget (in cents)</label>
                <input
                  type="number"
                  name="dailyBudget"
                  value={editedPost.dailyBudget || ""}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="100 = $1.00"
                  disabled={isPublished}
                />
              </div>
            )}

            {/* Lifetime Budget Fields */}
            {editedPost.budgetStrategy === 'lifetime_budget' && (
              <div className="form-group">
                <label>Lifetime Budget (in cents)</label>
                <input
                  type="number"
                  name="lifetimeBudget"
                  value={editedPost.lifetimeBudget || ""}
                  onChange={handleChange}
                  className="form-control"
                  placeholder="1000 = $10.00"
                  disabled={isPublished}
                />
              </div>
            )}

            {/* Start Date & Time - Show for both daily and lifetime budget */}
            <div className="form-group">
              <label>Start Date & Time</label>
              <input
                type="datetime-local"
                name="startDate"
                value={editedPost.startDate || ""}
                onChange={handleChange}
                className="form-control"
                disabled={isPublished}
                min={(() => {
                  const tz = (editedPost?.event && editedPost.event.timezone) || editedPost?.timezone || 'UTC';
                  const eventStart = editedPost?.event?.start_date;
                  const now = new Date();
                  const minDate = eventStart ? new Date(eventStart) : now;
                  return minDate > now ? toLocalInputForTz(minDate, tz) : nowLocalInputForTz(tz);
                })()}
                max={(() => {
                  const tz = (editedPost?.event && editedPost.event.timezone) || editedPost?.timezone || 'UTC';
                  const eventEnd = editedPost?.event?.end_date;
                  return eventEnd ? toLocalInputForTz(eventEnd, tz) : '';
                })()}
              />
            </div>

            {/* End Date & Time - Show for both daily and lifetime budget */}
            <div className="form-group">
              <label>End Date & Time</label>
              <input
                type="datetime-local"
                name="endDate"
                value={editedPost.endDate || ""}
                onChange={handleChange}
                className="form-control"
                disabled={isPublished}
                min={(() => {
                  const tz = (editedPost?.event && editedPost.event.timezone) || editedPost?.timezone || 'UTC';
                  const eventStart = editedPost?.event?.start_date;
                  const startDateValue = editedPost.startDate;
                  const now = new Date();
                  // Use startDate if set, otherwise use event start or now
                  let minDate = startDateValue ? new Date(startDateValue) : (eventStart ? new Date(eventStart) : now);
                  minDate = minDate > now ? minDate : now;
                  return toLocalInputForTz(minDate, tz);
                })()}
                max={(() => {
                  const tz = (editedPost?.event && editedPost.event.timezone) || editedPost?.timezone || 'UTC';
                  const eventEnd = editedPost?.event?.end_date;
                  return eventEnd ? toLocalInputForTz(eventEnd, tz) : '';
                })()}
              />
            </div>

            {/* Billing Event */}
            <div className="form-group">
              <label>Billing Event</label>
              <select
                name="billingEvent"
                value={editedPost.billingEvent || "IMPRESSIONS"}
                onChange={handleChange}
                className="form-control auto-height-select"
                disabled={isPublished}
              >
                {billingEvents.map(event => (
                  <option key={event} value={event}>{event}</option>
                ))}
              </select>
            </div>

            {/* Optimization Goal */}
            <div className="form-group">
              <label>Optimization Goal</label>
              <select
                name="optimizationGoal"
                value={editedPost.optimizationGoal || "LINK_CLICKS"}
                onChange={handleChange}
                className="form-control auto-height-select"
                disabled={isPublished}
              >
                {optimizationGoals.map(goal => (
                  <option key={goal} value={goal}>{goal}</option>
                ))}
              </select>
            </div>

            {/* Bid Strategy */}
            <div className="form-group">
              <label>Bid Strategy</label>
              <select
                name="bidStrategy"
                value={editedPost.bidStrategy || "LOWEST_COST_WITHOUT_CAP"}
                onChange={handleChange}
                className="form-control auto-height-select"
                disabled
              >
                {bidStrategies.map(strategy => (
                  <option key={strategy} value={strategy}>{strategy}</option>
                ))}
              </select>
            </div>

            {/* Geo Location */}
            <div className="form-group">
              <label>Geo Location</label>
              <input
                type="text"
                name="geoLocation"
                value={editedPost.geoLocation || "US"}
                onChange={handleChange}
                className="form-control"
                disabled
                placeholder="US (default) or City, Region, Country"
              />
            </div>

            {/* Age Min */}
            <div className="form-group">
              <label>Age Min</label>
              <input
                type="number"
                name="ageMin"
                value={editedPost.ageMin || ""}
                onChange={handleChange}
                className="form-control"
                min="13"
                max="65"
                disabled={isPublished}
              />
            </div>

            {/* Age Max */}
            <div className="form-group">
              <label>Age Max</label>
              <input
                type="number"
                name="ageMax"
                value={editedPost.ageMax || ""}
                onChange={handleChange}
                className="form-control"
                min="13"
                max="65"
                disabled={isPublished}
              />
            </div>

            {/* Creative Name */}
            <div className="form-group">
              <label>Creative Name</label>
              <input
                type="text"
                name="creativeName"
                value={editedPost.creativeName || ""}
                onChange={handleChange}
                className="form-control"
                disabled={isPublished}
              />
            </div>

            {/* Creative Message */}
            <div className="form-group">
              <label>Creative Message</label>
              <textarea
                name="creativeMessage"
                value={editedPost.creativeMessage || ""}
                onChange={handleChange}
                className="form-control description"
                rows="3"
                disabled={isPublished}
              />
            </div>

            {/* Creative Headline */}
            <div className="form-group">
              <label>Creative Headline</label>
              <input
                type="text"
                name="creativeHeadline"
                value={editedPost.creativeHeadline || ""}
                onChange={handleChange}
                className="form-control"
                disabled={isPublished}
              />
            </div>

            {/* Creative Description */}
            <div className="form-group">
              <label>Creative Description</label>
              <textarea
                name="creativeDescription"
                value={editedPost.creativeDescription || ""}
                onChange={handleChange}
                className="form-control description"
                rows="3"
                disabled={isPublished}
              />
            </div>

            {/* Creative Link */}
            <div className="form-group">
              <label>Creative Link</label>
              <input
                type="url"
                name="creativeLink"
                value={editedPost.creativeLink || ""}
                onChange={handleChange}
                className="form-control"
                placeholder="https://example.com"
                disabled={isPublished}
              />
            </div>

            {/* Ad Name */}
            <div className="form-group">
              <label>Ad Name</label>
              <input
                type="text"
                name="adName"
                value={editedPost.adName || ""}
                onChange={handleChange}
                className="form-control"
                disabled={isPublished}
              />
            </div>

            {/* Meta Ads Status Information */}
            {(post?.status === "scheduled" || post?.status === "published" || post?.facebook_review_status) && (
              <div style={{ marginTop: '30px', paddingTop: '20px', borderTop: '1px solid #e0e0e0' }}>
                <h4 style={{ marginBottom: '15px', fontSize: '14px', fontWeight: '600', color: '#333' }}>Meta Ads Status</h4>
                
                {/* Last Updated */}
                {(post?.updated_at || editedPost?.updated_at) && (
                  <div className="form-group" style={{ marginBottom: '12px' }}>
                    <label style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>Last Updated</label>
                    <div style={{ fontSize: '13px', color: '#333', padding: '8px 0' }}>
                      {moment(post?.updated_at || editedPost?.updated_at).format('MMM D, YYYY h:mm A')}
                    </div>
                  </div>
                )}

                {/* Facebook Review Status */}
                {(post?.facebook_review_status || editedPost?.facebook_review_status) && (
                  <div className="form-group" style={{ marginBottom: '12px' }}>
                    <label style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>Facebook Review Status</label>
                    <div style={{ 
                      fontSize: '13px', 
                      color: (post?.facebook_review_status || editedPost?.facebook_review_status) === 'APPROVED' ? '#28a745' : 
                             (post?.facebook_review_status || editedPost?.facebook_review_status) === 'REJECTED' ? '#dc3545' : '#ffc107',
                      padding: '8px 0',
                      fontWeight: '500'
                    }}>
                      {(post?.facebook_review_status || editedPost?.facebook_review_status) === 'APPROVED' && 'Approved'}
                      {(post?.facebook_review_status || editedPost?.facebook_review_status) === 'REJECTED' && 'Rejected'}
                      {(post?.facebook_review_status || editedPost?.facebook_review_status) === 'IN_REVIEW' && 'In Review'}
                      {!['APPROVED', 'REJECTED', 'IN_REVIEW'].includes(post?.facebook_review_status || editedPost?.facebook_review_status) && (post?.facebook_review_status || editedPost?.facebook_review_status)}
                    </div>
                  </div>
                )}

                {/* Facebook Rejection Reason */}
                {(post?.facebook_rejection_reason || editedPost?.facebook_rejection_reason) && (
                  <div className="form-group" style={{ marginBottom: '12px' }}>
                    <label style={{ fontSize: '12px', color: '#666', fontWeight: '500' }}>Rejection Reason</label>
                    <div style={{ 
                      fontSize: '13px', 
                      color: '#dc3545',
                      padding: '8px 0',
                      backgroundColor: '#fff5f5',
                      borderRadius: '4px',
                      padding: '10px',
                      border: '1px solid #fecaca'
                    }}>
                      {post?.facebook_rejection_reason || editedPost?.facebook_rejection_reason}
                    </div>
                  </div>
                )}
              </div>
            )}
          </div>

          <div className="modal-footer">
            {/* Save Changes button */}
            {post && (post.status !== "published" && post.status !== "scheduled") && (
            <button
              type="submit"
              className="btns btn-save-changes"
              disabled={loading}
            >
              {loading ? 'Saving...' : 'Save Changes'}
            </button>
            )}
            
            {/* Show Approve button if ad status is not published */}
            {post && (post.status !== "published" && post.status !== "scheduled") && (
              <button
                type="button"
                className="btns btn-approve"
                disabled={loading}
                onClick={() => {
                  setLoading(true);
                  const updatedPost = {
                    ...editedPost,
                    status: "scheduled",
                  };
                  onSave(updatedPost, "schedule", () => {
                    setLoading(false);
                  }, true); // Pass true to close modal
                }}
              >
                {loading ? 'Approving...' : 'Approve'}
              </button>
            )}

            {/* Delete button */}
            {post && onDelete && (
              <button
                type="button"
                className="btns btn-delete"
                onClick={() => {
                  if (window.confirm('Are you sure you want to delete this ad? This action cannot be undone.')) {
                    onDelete(post.id);
                    onClose();
                  }
                }}
              >
                <svg width="16" height="16" viewBox="0 0 512 512" fill="none">
                  <path
                    d="M436 60h-89.185l-9.75-29.238A44.945 44.945 0 0 0 294.379 0h-76.758a44.975 44.975 0 0 0-42.7 30.762L165.182 60H76c-24.814 0-45 20.186-45 45v30c0 16.708 15.041 15 31.183 15H466c8.291 0 15-6.709 15-15v-30c0-24.814-20.186-45-45-45zm-239.187 0 6.57-19.746A14.996 14.996 0 0 1 217.621 30h76.758c6.46 0 12.188 4.116 14.224 10.254L315.18 60H196.813zM64.666 182l23.917 289.072C90.707 494.407 109.97 512 133.393 512h245.215c23.423 0 42.686-17.593 44.824-41.06L447.336 182H64.666zM181 437c0 19.773-30 19.854-30 0V227c0-19.773 30-19.854 30 0v210zm90 0c0 19.773-30 19.854-30 0V227c0-19.773 30-19.854 30 0v210zm90 0c0 19.773-30 19.854-30 0V227c0-8.291 6.709-15 15-15s15 6.709 15 15v210z"
                    fill="#ffffff"
                  />
                </svg>
              </button>
            )}

            <button
              type="button"
              className="btns btn-close"
              onClick={onClose}
            >
              Close
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default EditAdModal;

